import React, { useState, useEffect, useRef } from "react";
import "./LeaveManagement.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRegEye,
  FaRegStickyNote,
  FaEdit,
  FaCheck,
  FaTimes,
  FaTrash,
  FaPlus,
  FaCalendarAlt,
  FaPen,
  FaSave,
} from "react-icons/fa";

// ===== HELPER: Get current academic year start (April to March) =====
const getCurrentAcademicYearStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-12
  return month >= 4 ? year : year - 1;
};

const LeaveManagement = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newAllowance, setNewAllowance] = useState("");
  const [hoveredCell, setHoveredCell] = useState(null);
  const [clickedCell, setClickedCell] = useState(null);

  // ===== FIX: Auto-calculate current academic year =====
  const [academicYear, setAcademicYear] = useState(() => getCurrentAcademicYearStart());

  const [currentPage, setCurrentPage] = useState(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [newNotes, setNewNotes] = useState("");
  const [editMode, setEditMode] = useState(false);

  // Add Absence Modal state
  const [showAddAbsenceModal, setShowAddAbsenceModal] = useState(false);
  const [addAbsenceTarget, setAddAbsenceTarget] = useState({ userId: null, month: "" });
  const [newAbsenceDate, setNewAbsenceDate] = useState("");
  const [newAbsenceNotes, setNewAbsenceNotes] = useState("");

  const entriesPerPage = 15;
  const tooltipRef = useRef(null);

  const months = [
    "Apr", "May", "Jun", "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec", "Jan", "Feb", "Mar",
  ];

  const monthToNumber = {
    Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9,
    Oct: 10, Nov: 11, Dec: 12, Jan: 1, Feb: 2, Mar: 3,
  };

  const fixDocumentUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("/")) {
      return `https://backend.ems.uk.hiradentallab.com${url}`;
    }
    const urlObj = new URL(url, "https://backend.ems.uk.hiradentallab.com");
    urlObj.port = "5000";
    return urlObj.toString();
  };

  // ===== FIX: Get academic year string for API =====
  const getAcademicYearString = () => `${academicYear}-${academicYear + 1}`;

  // Fetch leave records
  const fetchLeaveRecords = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`[fetchLeaveRecords] Fetching for academic year: ${academicYear}`);

      const response = await axios.get(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/yearly/${academicYear}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      if (response.data.success) {
        console.log(`[fetchLeaveRecords] Got ${response.data.absences?.length || 0} records`);

        const processedData = response.data.absences.map((record) => {
          const monthlyData = { ...record.monthlyData };
          months.forEach((month) => {
            if (!(month in monthlyData)) {
              monthlyData[month] = { totalAbsences: 0, details: [] };
            } else {
              monthlyData[month] = {
                totalAbsences: monthlyData[month].totalAbsences || 0,
                details: (monthlyData[month].details || []).map((absence) => ({
                  ...absence,
                  date: new Date(absence.date).toISOString().split("T")[0],
                  notes: absence.notes || absence.reason || "",
                })),
              };
            }
          });
          return {
            ...record,
            monthlyData,
            holidayAllowance: record.yearlyLeaveCount || "Set",
          };
        });
        setLeaveData(processedData);
        setCurrentPage(1);
      } else {
        throw new Error(response.data.message || "Failed to fetch leave records");
      }
    } catch (error) {
      console.error("Error fetching leave records:", error);
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveRecords();
  }, [academicYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        const isAbsenceCell = event.target.closest(".has-absences, .absence-cell");
        if (!isAbsenceCell) {
          setClickedCell(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  // Allowance Modal
  const handleSetOrEditAllowance = (user) => {
    setSelectedUser(user);
    setNewAllowance(user.yearlyLeaveCount || "");
    setShowModal(true);
    setError(null);
  };

  const handleSaveAllowance = async () => {
    if (!newAllowance || isNaN(newAllowance) || newAllowance <= 0) {
      setError("Leave count must be a number greater than 0");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(
        "https://backend.ems.uk.hiradentallab.com/api/leavesmanual/createLeaveEntry",
        {
          userId: selectedUser.userId,
          leaveCount: Number(newAllowance),
          academicYear: getAcademicYearString(), // ===== FIX: Use correct academic year
        },
        { headers: { "Content-Type": "application/json" }, withCredentials: false }
      );

      if (response.data.success) {
        setSuccessMessage(
          `Leave allowance successfully ${
            selectedUser.yearlyLeaveCount ? "updated" : "set"
          } for ${selectedUser.name}`
        );
        setShowModal(false);
        await fetchLeaveRecords();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error saving allowance:", error);
      const errorMsg = error.response?.data?.message
        || error.response?.data?.error
        || error.message;
      setError(`Server Error: ${errorMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setNewAllowance("");
    setSelectedUser(null);
    setError(null);
  };

  // Notes Modal
  const handleNotesClick = (absence, userId, month) => {
    const noteContent = absence.notes || absence.reason || "";
    setSelectedAbsence({ ...absence, userId, month });
    setNewNotes(noteContent);
    setShowNotesModal(true);
    setEditMode(false);
    setError(null);
  };

  const handleSaveNotes = async () => {
    if (!selectedAbsence) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await axios.put(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/absences/notes",
        {
          userId: selectedAbsence.userId,
          academicYear: getAcademicYearString(), // ===== FIX: Use correct academic year
          month: selectedAbsence.month,
          date: selectedAbsence.date,
          notes: newNotes,
        },
        { headers: { "Content-Type": "application/json" }, withCredentials: false }
      );

      if (response.data.success) {
        setSuccessMessage(
          `Notes successfully ${selectedAbsence.notes ? "updated" : "added"} for ${selectedAbsence.date}`
        );
        setShowNotesModal(false);
        await fetchLeaveRecords();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      setError(error.response?.data?.message || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelNotes = () => {
    setShowNotesModal(false);
    setSelectedAbsence(null);
    setNewNotes("");
    setEditMode(false);
    setError(null);
  };

  // Add Absence Functions
  const openAddAbsenceModal = (userId, month) => {
    setAddAbsenceTarget({ userId, month });
    const monthNum = monthToNumber[month];
    const year = monthNum >= 4 ? academicYear : academicYear + 1;
    setNewAbsenceDate(`${year}-${String(monthNum).padStart(2, "0")}-01`);
    setNewAbsenceNotes("");
    setShowAddAbsenceModal(true);
    setClickedCell(null);
    setError(null);
  };

  const handleAddAbsence = async () => {
    if (!newAbsenceDate) {
      setError("Please select a date");
      return;
    }

    const selectedDate = new Date(newAbsenceDate);
    const monthNum = monthToNumber[addAbsenceTarget.month];
    const expectedYear = monthNum >= 4 ? academicYear : academicYear + 1;

    if (selectedDate.getMonth() + 1 !== monthNum || selectedDate.getFullYear() !== expectedYear) {
      setError(`Date must be in ${addAbsenceTarget.month} ${expectedYear}`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/absences/manual",
        {
          userId: addAbsenceTarget.userId,
          academicYear: getAcademicYearString(), // ===== FIX: Use correct academic year
          date: newAbsenceDate,
          month: addAbsenceTarget.month,
          notes: newAbsenceNotes,
        },
        { headers: { "Content-Type": "application/json" }, withCredentials: false }
      );

      if (response.data.success) {
        setSuccessMessage("Absence added successfully");
        setShowAddAbsenceModal(false);
        await fetchLeaveRecords();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error adding absence:", error);
      const errorMsg = error.response?.data?.message
        || error.response?.data?.error
        || error.message;
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!absenceId) {
      console.error("No absenceId provided");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this absence?")) return;

    setIsSubmitting(true);
    try {
      console.log("Deleting absence:", absenceId);
      const response = await axios.delete(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/absences/manual",
        {
          data: { absenceId },
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      if (response.data.success) {
        setSuccessMessage("Absence deleted successfully");
        setClickedCell(null);
        await fetchLeaveRecords();
      }
    } catch (error) {
      console.error("Error deleting absence:", error);
      const errorMsg = error.response?.data?.message
        || error.response?.data?.error
        || error.message;
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAddAbsence = () => {
    setShowAddAbsenceModal(false);
    setAddAbsenceTarget({ userId: null, month: "" });
    setNewAbsenceDate("");
    setNewAbsenceNotes("");
    setError(null);
  };

  // Tooltip handlers
  const handleCellHover = (event, userId, month, details) => {
    const tooltipWidth = 320;
    const viewportWidth = window.innerWidth;
    let leftPosition = event.clientX + 20;
    if (leftPosition + tooltipWidth > viewportWidth) {
      leftPosition = event.clientX - tooltipWidth - 20;
    }
    const tooltipHeight = 250;
    const viewportHeight = window.innerHeight;
    let topPosition = event.clientY - tooltipHeight - 10;
    if (topPosition < 0) topPosition = event.clientY + 10;

    setHoveredCell({
      userId,
      month,
      details,
      clientX: leftPosition,
      clientY: topPosition,
    });
  };

  const handleCellClick = (event, userId, month, details) => {
    const tooltipWidth = 320;
    const viewportWidth = window.innerWidth;
    let leftPosition = event.clientX + 20;
    if (leftPosition + tooltipWidth > viewportWidth) {
      leftPosition = event.clientX - tooltipWidth - 20;
    }
    const tooltipHeight = 250;
    const viewportHeight = window.innerHeight;
    let topPosition = event.clientY - tooltipHeight - 10;
    if (topPosition < 0) topPosition = event.clientY + 10;

    const newClickedCell = {
      userId,
      month,
      details,
      clientX: leftPosition,
      clientY: topPosition,
    };

    if (clickedCell && clickedCell.userId === userId && clickedCell.month === month) {
      setClickedCell(null);
    } else {
      setClickedCell(newClickedCell);
    }
  };

  const filteredData = leaveData
    .filter(
      (row) =>
        row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (row.department && row.department.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const handlePrevYear = () => {
    setAcademicYear((prev) => prev - 1);
    setClickedCell(null);
  };

  const handleNextYear = () => {
    setAcademicYear((prev) => prev + 1);
    setClickedCell(null);
  };

  const toggleEditMode = () => setEditMode(!editMode);

  return (
    <div className="leave-management">
      <div className="header-section">
        <div className="header-left">
          <h1 style={{ color: "#0353a4", marginTop: "60px", textAlign: "left", fontSize: "2rem" }}>
            Leave Records
          </h1>
          {/* ===== FIX: Show correct date range ===== */}
          <span className="date-range">{`1 Apr ${academicYear} - 31 Mar ${academicYear + 1}`}</span>
          <hr />
        </div>
      </div>

      <AnimatePresence>
        {(successMessage || error) && (
          <motion.div
            className={successMessage ? "success-message" : "error-message"}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {successMessage || error}
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          Loading leave records...
        </div>
      ) : (
        <div className="table-container">
          <div className="table-controls">
            <div className="date-picker">
              <button onClick={handlePrevYear} className="arrow-btn">◀</button>
              {/* ===== FIX: Show correct year range ===== */}
              <span className="year-range">{`${academicYear} - ${academicYear + 1}`}</span>
              <button onClick={handleNextYear} className="arrow-btn">▶</button>
            </div>
            <input
              type="text"
              placeholder="Search by name or department"
              className="search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <table className="allowance-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Holiday Allowance</th>
                <th>Holiday Used</th>
                <th>Holiday Remaining</th>
                {months.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentEntries.length > 0 ? (
                currentEntries.map((row) => (
                  <tr key={row.userId}>
                    <td>{row.name}</td>
                    <td>
                      {!row.yearlyLeaveCount ? (
                        <button onClick={() => handleSetOrEditAllowance(row)} className="set-allowance-btn">
                          Set
                        </button>
                      ) : (
                        <span className="allowance-value clickable" onClick={() => handleSetOrEditAllowance(row)}>
                          {row.yearlyLeaveCount} DAYS
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: "bold" }}>{row.usedLeaves || 0} DAYS</td>
                    <td style={{ fontWeight: "bold" }} className={row.remainingLeaves < 0 ? "negative" : ""}>
                      {row.remainingLeaves} DAYS
                    </td>
                    {months.map((month) => (
                      <td
                        key={month}
                        className={`absence-cell ${row.monthlyData[month]?.totalAbsences > 0 ? "has-absences" : ""}`}
                        onMouseEnter={(e) =>
                          handleCellHover(e, row.userId, month, row.monthlyData[month]?.details || [])
                        }
                        onMouseLeave={() => setHoveredCell(null)}
                        onClick={(e) =>
                          handleCellClick(e, row.userId, month, row.monthlyData[month]?.details || [])
                        }
                      >
                        {row.monthlyData[month]?.totalAbsences || 0}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={17} className="no-data">No matching records found</td>
                </tr>
              )}
            </tbody>
            <tfoot className="table-footer">
              <tr className="emps-payroll-table-footer">
                <td>Total</td>
                <td>{currentEntries.length}</td>
                <td></td>
                <td colSpan={14}></td>
              </tr>
            </tfoot>
          </table>

          <div className="pagination-container">
            <div className="pagination-info">
              Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredData.length)} of{" "}
              {filteredData.length} entries
            </div>
            <div className="pagination-buttons">
              <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn">
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`pagination-btn ${currentPage === number ? "active" : ""}`}
                >
                  {number}
                </button>
              ))}
              <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn">
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Tooltip */}
      <AnimatePresence>
        {(hoveredCell || clickedCell) && (
          <motion.div
            ref={tooltipRef}
            className="absence-tooltip enhanced-tooltip"
            style={{
              top: (clickedCell || hoveredCell).clientY,
              left: (clickedCell || hoveredCell).clientX,
              pointerEvents: clickedCell ? "auto" : "none",
            }}
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="tooltip-arrow" />
            <div className="tooltip-header">
              <h4>Absence Details</h4>
              <div className="tooltip-subheader">
                <span className="tooltip-month-badge">
                  {(clickedCell || hoveredCell).month}
                </span>
                <span className="tooltip-day-count">
                  {(clickedCell || hoveredCell).details.length} day{(clickedCell || hoveredCell).details.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="tooltip-content">
              {(clickedCell || hoveredCell).details.length === 0 && (
                <div className="no-absences" style={{ textAlign: "center", color: "#999", padding: "10px", fontSize: "0.9rem" }}>
                  No absences recorded
                </div>
              )}
              {(clickedCell || hoveredCell).details.map((absence, index) => (
                <motion.div
                  key={absence._id || index}
                  className="absence-detail-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="detail-row">
                    <div className="absence-date">{formatDate(absence.date)}</div>
                    <div className="absence-actions">
                      {absence.document && (
                        <a
                          href={fixDocumentUrl(absence.document)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-icon doc-link"
                          title="View document"
                        >
                          <FaRegEye />
                        </a>
                      )}
                      <button
                        className="action-icon notes-btn"
                        onClick={() =>
                          handleNotesClick(
                            absence,
                            (clickedCell || hoveredCell).userId,
                            (clickedCell || hoveredCell).month
                          )
                        }
                        title={absence.notes || absence.reason ? "View/Edit Notes" : "Add Notes"}
                      >
                        <FaRegStickyNote />
                      </button>
                      {absence._id && (
                        <button
                          className="action-icon delete-btn"
                          onClick={() => handleDeleteAbsence(absence._id)}
                          title="Delete Absence"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                  {(absence.reason || absence.notes) && (
                    <div className="absence-reason-snippet">
                      {absence.reason || absence.notes}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            {clickedCell && (
              <div className="tooltip-footer">
                <button
                  className="add-absence-btn enhanced"
                  onClick={() => openAddAbsenceModal(clickedCell.userId, clickedCell.month)}
                >
                  <FaPlus /> Add Absence
                </button>
              </div>
            )}
            {clickedCell && (
              <button className="close-tooltip-btn" onClick={() => setClickedCell(null)}>×</button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Allowance Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <motion.div
            className="modal-content allowance-modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 20 }}
          >
            <div className="modal-header allowance-header">
              <h2>
                {selectedUser?.yearlyLeaveCount ? "Edit Leave Allowance" : "Set Leave Allowance"} for {selectedUser?.name}
              </h2>
              <button className="close-btn" onClick={handleCancelEdit} disabled={isSubmitting}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="leaveCount">Holiday Allowance (days):</label>
                <input
                  type="number"
                  id="leaveCount"
                  value={newAllowance}
                  onChange={(e) => setNewAllowance(e.target.value)}
                  className="allowance-input"
                  min="1"
                  placeholder="Enter number of days"
                />
              </div>
              {error && (
                <motion.div className="modal-error" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  {error}
                </motion.div>
              )}
            </div>
            <div className="modal-footer allowance-footer">
              <button type="button" className="cancel-btn" onClick={handleCancelEdit} disabled={isSubmitting}>Cancel</button>
              <button type="button" className="submit-btn allowance-submit-btn" onClick={handleSaveAllowance} disabled={isSubmitting}>
                {isSubmitting ? <><span className="spinner"></span> Saving...</> : selectedUser?.yearlyLeaveCount ? "Update Allowance" : "Set Allowance"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Enhanced Notes Modal */}
      <AnimatePresence>
        {showNotesModal && selectedAbsence && (
          <div className="modal-backdrop" onClick={handleCancelNotes}>
            <motion.div
              className="notes-modal-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="notes-card-header">
                <div className="header-left">
                  <FaRegStickyNote className="header-icon" />
                  <div>
                    <h3>Leave Notes</h3>
                    <span className="date-badge">
                      <FaCalendarAlt /> {formatDate(selectedAbsence.date)}
                    </span>
                  </div>
                </div>
                <button className="close-btn-icon" onClick={handleCancelNotes} disabled={isSubmitting}>
                  <FaTimes />
                </button>
              </div>

              <div className="notes-card-body">
                {editMode ? (
                  <div className="edit-area">
                    <textarea
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      className="notes-textarea enhanced"
                      placeholder="Add details about this absence..."
                      rows={5}
                      autoFocus
                    />
                    <div className="char-count">{newNotes.length} characters</div>
                  </div>
                ) : newNotes ? (
                  <div className="notes-display-card">
                    {newNotes}
                  </div>
                ) : (
                  <div className="empty-notes">
                    <FaPen className="empty-icon" />
                    <p>No notes yet. Click edit to add.</p>
                  </div>
                )}
              </div>

              <div className="notes-card-footer">
                {editMode ? (
                  <>
                    <button className="btn btn-cancel" onClick={() => setEditMode(false)}>
                      <FaTimes /> Cancel
                    </button>
                    <button
                      className="btn btn-save"
                      onClick={handleSaveNotes}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <span className="spinner" /> : <FaSave />}
                      {isSubmitting ? " Saving..." : " Save"}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-edit" onClick={toggleEditMode}>
                    <FaEdit /> Edit Notes
                  </button>
                )}
              </div>
              {error && (
                <div className="notes-error-message">
                  <FaTimes className="error-icon" /> {error}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Enhanced Add Absence Modal */}
      <AnimatePresence>
        {showAddAbsenceModal && (
          <div className="modal-backdrop" onClick={handleCancelAddAbsence}>
            <motion.div
              className="absence-modal-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="modal-card-header">
                <div>
                  <h2>Add Absence</h2>
                  <span className="month-indicator">
                    <FaCalendarAlt /> {addAbsenceTarget.month} ({academicYear}-{academicYear + 1})
                  </span>
                </div>
                <button className="close-btn-icon" onClick={handleCancelAddAbsence} disabled={isSubmitting}>
                  <FaTimes />
                </button>
              </div>

              <div className="modal-card-body">
                <div className="input-group">
                  <label>
                    <FaCalendarAlt className="input-icon" /> Select Date
                  </label>
                  <input
                    type="date"
                    value={newAbsenceDate}
                    onChange={(e) => setNewAbsenceDate(e.target.value)}
                    className="modern-date-input"
                    required
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaPen className="input-icon" /> Reason / Notes
                  </label>
                  <textarea
                    value={newAbsenceNotes}
                    onChange={(e) => setNewAbsenceNotes(e.target.value)}
                    className="notes-textarea enhanced"
                    placeholder="Optional reason for absence..."
                    rows={4}
                  />
                </div>

                {error && (
                  <div className="modal-error-message">
                    <FaTimes /> {error}
                  </div>
                )}
              </div>

              <div className="modal-card-footer">
                <button className="btn btn-cancel" onClick={handleCancelAddAbsence} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddAbsence}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <><span className="spinner" /> Adding...</> : <><FaPlus /> Add Absence</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveManagement;